import { RingProgress } from '@mantine/core';
import {
  IconTicket,
  IconClock,
  IconStar,
  IconTrendingUp,
  IconTrendingDown,
} from '@tabler/icons-react';
import { Box, Card, Group, Progress, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  helpdeskStats,
  ticketsByStatus,
  ticketsByPriority,
  ticketsByCategory,
} from '../mockData/helpdesk';

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color,
  isPositive = true
}: {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ElementType;
  color: string;
  isPositive?: boolean;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    {change !== undefined && (
      <Group gap="xs" mt="xs">
        {isPositive ? (
          <IconTrendingUp size={16} color="var(--mantine-color-green-filled)" />
        ) : (
          <IconTrendingDown size={16} color="var(--mantine-color-red-filled)" />
        )}
        <Text size="xs" c={isPositive ? 'green' : 'red'}>
          {change >= 0 ? '+' : ''}{change}%
        </Text>
        <Text size="xs" c="dimmed">vs last week</Text>
      </Group>
    )}
  </Card>
);

export default function ReportsPage() {
  const totalTickets = ticketsByStatus.reduce((acc, s) => acc + s.count, 0);
  const maxCategoryCount = Math.max(...ticketsByCategory.map(c => c.count));

  return (
    <Stack gap="lg">
      {/* Header */}
      <Box>
        <Title order={2}>Support Reports</Title>
        <Text c="dimmed" size="sm">Performance metrics and analytics</Text>
      </Box>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Tickets This Week"
          value={helpdeskStats.ticketsThisWeek}
          change={helpdeskStats.ticketsChange}
          icon={IconTicket}
          color="blue"
        />
        <StatCard
          title="Avg Response Time"
          value={`${helpdeskStats.avgResponseTime} min`}
          change={-8}
          icon={IconClock}
          color="green"
          isPositive={true}
        />
        <StatCard
          title="Avg Resolution Time"
          value={`${Math.round(helpdeskStats.avgResolutionTime / 60)} hrs`}
          change={-5}
          icon={IconClock}
          color="orange"
          isPositive={true}
        />
        <StatCard
          title="Customer Satisfaction"
          value={`${helpdeskStats.customerSatisfaction}/5`}
          change={2.5}
          icon={IconStar}
          color="yellow"
        />
      </SimpleGrid>

      {/* SLA and Performance */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* SLA Compliance */}
        <Card>
          <Text fw={600} mb="md">SLA Compliance</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={[
                { value: helpdeskStats.slaCompliance, color: 'green' },
                { value: 100 - helpdeskStats.slaCompliance, color: 'red' },
              ]}
              label={
                <Box ta="center">
                  <Text size="xl" fw={700}>{helpdeskStats.slaCompliance}%</Text>
                  <Text size="xs" c="dimmed">Compliance</Text>
                </Box>
              }
            />
            <Stack gap="md" style={{ flex: 1 }}>
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Within SLA</Text>
                  <Text size="sm" fw={500} c="green">{helpdeskStats.slaCompliance}%</Text>
                </Group>
                <Progress value={helpdeskStats.slaCompliance} size="sm" color="green" />
              </Box>
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">SLA Breached</Text>
                  <Text size="sm" fw={500} c="red">{100 - helpdeskStats.slaCompliance}%</Text>
                </Group>
                <Progress value={100 - helpdeskStats.slaCompliance} size="sm" color="red" />
              </Box>
            </Stack>
          </Group>
        </Card>

        {/* Resolution Rate */}
        <Card>
          <Text fw={600} mb="md">Resolution Metrics</Text>
          <Stack gap="md">
            <Box p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
              <Group justify="space-between">
                <Text size="sm">First Contact Resolution</Text>
                <Text size="lg" fw={700}>68%</Text>
              </Group>
              <Progress value={68} size="sm" mt="xs" color="blue" />
            </Box>
            <Box p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
              <Group justify="space-between">
                <Text size="sm">Escalation Rate</Text>
                <Text size="lg" fw={700}>12%</Text>
              </Group>
              <Progress value={12} size="sm" mt="xs" color="orange" />
            </Box>
            <Box p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
              <Group justify="space-between">
                <Text size="sm">Reopen Rate</Text>
                <Text size="lg" fw={700}>5%</Text>
              </Group>
              <Progress value={5} size="sm" mt="xs" color="red" />
            </Box>
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Distribution Charts */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* By Priority */}
        <Card>
          <Text fw={600} mb="md">Tickets by Priority</Text>
          <Stack gap="sm">
            {ticketsByPriority.map(priority => (
              <Group key={priority.priority} justify="space-between">
                <Group gap="sm" style={{ flex: 1 }}>
                  <Box
                    w={12}
                    h={12}
                    style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${priority.color}-filled)` }}
                  />
                  <Text size="sm">{priority.priority}</Text>
                </Group>
                <Group gap="sm" style={{ flex: 1 }}>
                  <Progress
                    value={(priority.count / totalTickets) * 100}
                    size="lg"
                    radius="xl"
                    style={{ flex: 1 }}
                    color={priority.color}
                  />
                </Group>
                <Text size="sm" fw={500} w={30} ta="right">{priority.count}</Text>
              </Group>
            ))}
          </Stack>
        </Card>

        {/* By Category */}
        <Card>
          <Text fw={600} mb="md">Tickets by Category</Text>
          <Stack gap="sm">
            {ticketsByCategory.map(category => (
              <Group key={category.category} justify="space-between">
                <Text size="sm" style={{ flex: 1 }}>{category.category}</Text>
                <Group gap="sm" style={{ flex: 1 }}>
                  <Progress
                    value={(category.count / maxCategoryCount) * 100}
                    size="lg"
                    radius="xl"
                    style={{ flex: 1 }}
                    color="blue"
                  />
                </Group>
                <Text size="sm" fw={500} w={30} ta="right">{category.count}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Agent Performance */}
      <Card>
        <Text fw={600} mb="md">Team Performance Summary</Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Box p="md" ta="center" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
            <Text size="xl" fw={700} c="blue">{helpdeskStats.resolvedToday}</Text>
            <Text size="xs" c="dimmed">Resolved Today</Text>
          </Box>
          <Box p="md" ta="center" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
            <Text size="xl" fw={700} c="green">{helpdeskStats.customerSatisfaction}</Text>
            <Text size="xs" c="dimmed">Avg. Rating</Text>
          </Box>
          <Box p="md" ta="center" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
            <Text size="xl" fw={700} c="orange">{helpdeskStats.avgResponseTime}m</Text>
            <Text size="xs" c="dimmed">Avg. Response</Text>
          </Box>
          <Box p="md" ta="center" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
            <Text size="xl" fw={700} c="violet">{helpdeskStats.slaCompliance}%</Text>
            <Text size="xs" c="dimmed">SLA Met</Text>
          </Box>
        </SimpleGrid>
      </Card>
    </Stack>
  );
}
